/**
 * Sharing a note with people (PLAN.md D4, D5, D6, "Sharing data").
 *
 * The share sheet's model and its calls to `routes/sharing.ts` on the
 * worker, plus the small rules the lists, the editor and sync share: whose a
 * note is, what the person may do with it, and the notices when their access
 * changes. Only the owner manages sharing; members see names (never emails)
 * and can leave. Inviting and an edit link are Pro for the owner (402).
 */

import { inviteRoles, memberRoles } from "@caliu/utils";

export type NoteRole = "owner" | "editor" | "viewer";
export type MemberRole = "editor" | "viewer";
export type LinkAccess = "none" | "view" | "edit";

export interface SharingMember {
  userId: string;
  name: string | null;
  /** The owner only. */
  email?: string | null;
  role: MemberRole;
  via: string;
  createdAt: string;
}

export interface SharingInvite {
  id: string;
  email: string;
  role: MemberRole;
  createdAt: string;
  expiresAt: string;
}

/** `GET /notes/:id/sharing`. `linkAccess`, `shareUrl` and `invites` reach the owner only. */
export interface SharingState {
  noteId: string;
  role: NoteRole;
  owner: { userId: string; name: string | null; email?: string | null };
  members: SharingMember[];
  linkAccess?: LinkAccess;
  shareUrl?: string | null;
  invites?: SharingInvite[];
}

// ── Whose a note is ───────────────────────────────────────────────────────

/** The sharing fields a local note carries (from pulls). */
export interface NoteSharingFields {
  ownerId?: string;
  ownerName?: string;
  role?: NoteRole;
  memberCount?: number;
  linkAccess?: LinkAccess;
  sharedAt?: string | null;
}

/** A note another account owns and shared with this one. */
export function isSharedWithMe(note: NoteSharingFields | undefined, meId: string | null | undefined): boolean {
  return !!note?.ownerId && !!meId && note.ownerId !== meId;
}

/** A note of this account that someone else can open (people, or the link). */
export function isSharedByMe(note: NoteSharingFields | undefined, meId: string | null | undefined): boolean {
  if (!note || isSharedWithMe(note, meId)) return false;
  return (note.memberCount ?? 0) > 0 || (note.linkAccess != null && note.linkAccess !== "none") || !!note.sharedAt;
}

/** Shared at all, either way: the list badge. */
export function isShared(note: NoteSharingFields | undefined, meId: string | null | undefined): boolean {
  return isSharedWithMe(note, meId) || isSharedByMe(note, meId);
}

/**
 * A viewer reads only (on top of the person's own read-only toggle). So does
 * any member while this device syncs in legacy mode (`sync_v2` off for the
 * account): the server refuses a member's legacy push.
 */
export function isViewOnly(
  note: NoteSharingFields | undefined,
  meId: string | null | undefined,
  legacy = false,
): boolean {
  return isSharedWithMe(note, meId) && (note?.role === "viewer" || legacy);
}

/**
 * SQL over `notes` for the notes this account owns: unshared (no owner) or
 * owned by `?`. The free note limit counts only these (PLAN.md "Rules").
 */
export const OWNED_NOTE_SQL = "(owner_id IS NULL OR owner_id = '' OR owner_id = ?)";

/** SQL over `notes` for notes shared with this account (`?` is the account). */
export const SHARED_WITH_ME_SQL = "(owner_id IS NOT NULL AND owner_id != '' AND owner_id != ?)";

/** SQL over `notes` for this account's notes someone else can open (`?` is the account). */
export const SHARED_BY_ME_SQL = `(${OWNED_NOTE_SQL} AND (member_count > 0 OR link_access IN ('view', 'edit') OR shared_at IS NOT NULL))`;

// ── The sheet's model ─────────────────────────────────────────────────────

export interface PersonRow {
  key: string;
  /** Member user id; null for the owner and for invites. */
  userId: string | null;
  inviteId: string | null;
  label: string;
  /** The avatar's letters, from the real name or address (never "You"). */
  initials: string;
  /** A second line (an email, for the owner's eyes only). */
  detail: string | null;
  role: NoteRole;
  isMe: boolean;
  pending: boolean;
  /** The owner can change this row's role or remove it. */
  editable: boolean;
}

export interface SheetModel {
  isOwner: boolean;
  /** Roles a new invite may get next to the link (one: no choice, just the label). */
  inviteRoles: MemberRole[];
  /** Roles a member or pending invite may be set to next to the link. */
  memberRoles: MemberRole[];
  /** Members leave; the owner cannot. */
  canLeave: boolean;
  people: PersonRow[];
  linkAccess: LinkAccess;
  shareUrl: string | null;
}

/**
 * The people list in sheet order: the owner first, then members as they
 * joined, then pending invites. "You" names the reader's own row; members
 * see names, never addresses (the server sends none to them).
 */
export function sheetModel(state: SharingState, meId: string, labels: { you: string }): SheetModel {
  const isOwner = state.role === "owner";
  const name = (n: string | null | undefined, email?: string | null) => n?.trim() || email || "";
  const owner: PersonRow = {
    key: `owner:${state.owner.userId}`,
    userId: null,
    inviteId: null,
    label: state.owner.userId === meId ? labels.you : name(state.owner.name, state.owner.email),
    initials: initialsOf(name(state.owner.name, state.owner.email)),
    detail: isOwner ? (state.owner.email ?? null) : null,
    role: "owner",
    isMe: state.owner.userId === meId,
    pending: false,
    editable: false,
  };
  const members: PersonRow[] = state.members.map((m) => ({
    key: `member:${m.userId}`,
    userId: m.userId,
    inviteId: null,
    label: m.userId === meId ? labels.you : name(m.name, isOwner ? m.email : null),
    initials: initialsOf(name(m.name, isOwner ? m.email : null)),
    detail: isOwner && m.name?.trim() ? (m.email ?? null) : null,
    role: m.role,
    isMe: m.userId === meId,
    pending: false,
    editable: isOwner,
  }));
  const invites: PersonRow[] = isOwner
    ? (state.invites ?? []).map((i) => ({
        key: `invite:${i.id}`,
        userId: null,
        inviteId: i.id,
        label: i.email,
        initials: initialsOf(i.email),
        detail: null,
        role: i.role,
        isMe: false,
        pending: true,
        editable: true,
      }))
    : [];
  const link = state.linkAccess ?? "none";
  return {
    isOwner,
    inviteRoles: inviteRoles(link),
    memberRoles: memberRoles(link),
    canLeave: !isOwner,
    people: [owner, ...members, ...invites],
    linkAccess: state.linkAccess ?? "none",
    shareUrl: state.shareUrl ?? null,
  };
}

/** Up to two letters for an avatar: first and last word of a name, or the start of an address. */
export function initialsOf(text: string): string {
  const base = text.includes("@") ? text.split("@")[0] : text;
  const words = base.split(/[\s._-]+/).filter(Boolean);
  if (words.length === 0) return "?";
  const first = Array.from(words[0])[0] ?? "";
  const last = words.length > 1 ? (Array.from(words[words.length - 1])[0] ?? "") : "";
  return (first + last).toUpperCase();
}

const EMAIL_RE = /^[^\s@,;]+@[^\s@,;]+\.[^\s@,;]+$/;

/** Splits the invite field (commas, semicolons, spaces), lower-cased and de-duplicated. */
export function parseInviteEmails(input: string): { emails: string[]; invalid: string[] } {
  const parts = input
    .split(/[,;\s]+/)
    .map((p) => p.trim().toLowerCase())
    .filter(Boolean);
  const emails: string[] = [];
  const invalid: string[] = [];
  for (const p of parts) {
    if (!EMAIL_RE.test(p)) invalid.push(p);
    else if (!emails.includes(p)) emails.push(p);
  }
  return { emails, invalid };
}

/**
 * Whether a change needs Pro before it is sent (the server answers 402
 * anyway): inviting, and turning an edit link on (one already on stays).
 */
export function needsPro(
  action: { kind: "invite" } | { kind: "link"; to: LinkAccess; from: LinkAccess },
  isPro: boolean,
): boolean {
  if (isPro) return false;
  if (action.kind === "invite") return true;
  return action.to === "edit" && action.from !== "edit";
}

/** The `sharing.*` string for a failed call. */
export function sharingErrorKey(err: unknown, fallback: string): string {
  if (typeof err !== "object" || err === null || typeof (err as { status?: unknown }).status !== "number") return fallback;
  const { status, code } = err as { status: number; code?: string };
  switch (code) {
    case "OWNER_ONLY":
      return "sharing.ownerOnly";
    case "SHARING_DISABLED":
      return "sharing.unavailable";
    case "MEMBER_LIMIT":
      return "sharing.memberLimit";
    case "INVITE_INVALID":
      return "sharing.inviteInvalid";
    case "ROLE_BELOW_LINK":
      return "sharing.roleBelowLink";
  }
  if (status === 429) return "sharing.rateLimit";
  if (status === 503) return "sharing.unavailable";
  return fallback;
}

// ── Opening the sheet with content (share-sheet plan, part 2) ───────────

/** What this device knows of a note's sharing before asking the server. */
export interface LocalSharing {
  state: SharingState;
  /** Nothing is missing: an owned note with no members (invites are refreshed behind it). */
  complete: boolean;
  /** Rows still on their way (members this device counts but cannot name). */
  placeholders: number;
}

/**
 * The sheet's first frame from the local note row (`owner_id`,
 * `member_count`, `link_access`): an owned note with no members is fully
 * known; any other shows its owner and `memberCount` placeholder rows until
 * the fetch lands.
 */
export function localSharingState(
  noteId: string,
  note: NoteSharingFields | undefined,
  me: { id: string; name: string | null; email?: string | null },
  webUrl: string,
): LocalSharing {
  const members = note?.memberCount ?? 0;
  if (isSharedWithMe(note, me.id)) {
    return {
      state: {
        noteId,
        role: note?.role ?? "viewer",
        owner: { userId: note!.ownerId!, name: note?.ownerName ?? null },
        members: [],
      },
      complete: false,
      placeholders: Math.max(members, 1),
    };
  }
  const linkAccess: LinkAccess = note?.linkAccess ?? (note?.sharedAt ? "view" : "none");
  return {
    state: {
      noteId,
      role: "owner",
      owner: { userId: me.id, name: me.name, email: me.email ?? null },
      members: [],
      linkAccess,
      shareUrl: linkAccess === "none" ? null : `${webUrl}/share/${noteId}`,
      invites: [],
    },
    complete: members === 0,
    placeholders: members,
  };
}

// ── Changes shown at once (share-sheet plan, part 6) ─────────────────────

/** A change the owner makes in the sheet, applied locally before the server answers. */
export type SharingChange =
  | { kind: "invite"; emails: string[]; role: MemberRole; now: string }
  | { kind: "memberRole"; userId: string; role: MemberRole }
  | { kind: "inviteRole"; inviteId: string; role: MemberRole }
  | { kind: "removeMember"; userId: string }
  | { kind: "revokeInvite"; inviteId: string }
  | { kind: "link"; access: LinkAccess; shareUrl: string | null };

/**
 * The state the server will hold once `change` lands, as far as this device
 * can tell, so the sheet shows it at once and the fetch after it reconciles.
 * Mirrors the worker: a new invite is a pending row (`local:<email>` until the
 * server names it; a known member or the owner is skipped, an address already
 * invited takes the new role), the link to edit raises viewers and pending
 * viewer invites, the link to view takes link joiners back to view, the link
 * off drops whoever joined through it. Swift and
 * Kotlin run the same `packages/utils/src/sharingChange.fixtures.json`.
 */
export function applySharingChange(state: SharingState, change: SharingChange): SharingState {
  const invites = state.invites ?? [];
  switch (change.kind) {
    case "invite": {
      const taken = new Set(
        [state.owner.email, ...state.members.map((m) => m.email)]
          .filter((e): e is string => !!e)
          .map((e) => e.toLowerCase()),
      );
      const next = [...invites];
      for (const raw of change.emails) {
        const email = raw.toLowerCase();
        if (taken.has(email)) continue;
        const at = next.findIndex((i) => i.email === email);
        if (at >= 0) next[at] = { ...next[at], role: change.role };
        else next.push({ id: `local:${email}`, email, role: change.role, createdAt: change.now, expiresAt: change.now });
      }
      return { ...state, invites: next };
    }
    case "memberRole":
      return {
        ...state,
        members: state.members.map((m) => (m.userId === change.userId ? { ...m, role: change.role } : m)),
      };
    case "inviteRole":
      return { ...state, invites: invites.map((i) => (i.id === change.inviteId ? { ...i, role: change.role } : i)) };
    case "removeMember":
      return { ...state, members: state.members.filter((m) => m.userId !== change.userId) };
    case "revokeInvite":
      return { ...state, invites: invites.filter((i) => i.id !== change.inviteId) };
    case "link": {
      const raise = change.access === "edit";
      return {
        ...state,
        linkAccess: change.access,
        shareUrl: change.access === "none" ? null : change.shareUrl,
        members: state.members
          .filter((m) => change.access !== "none" || m.via !== "link")
          .map((m) => {
            if (raise && m.role === "viewer") return { ...m, role: "editor" as const };
            // A link joiner's role is the link's: down with it to view.
            if (change.access === "view" && m.via === "link" && m.role === "editor") return { ...m, role: "viewer" as const };
            return m;
          }),
        invites: raise ? invites.map((i) => (i.role === "viewer" ? { ...i, role: "editor" as const } : i)) : invites,
      };
    }
  }
}

// ── The Share item's status line ─────────────────────────────────────────

/** What the Share menu item says under its title, from the local row only. */
export type ShareStatus =
  | { kind: "none" }
  | { kind: "people"; count: number }
  | { kind: "link"; access: "view" | "edit" }
  | { kind: "peopleAndLink"; count: number }
  | { kind: "sharedBy"; name: string | null; role: MemberRole };

/** A private note says nothing; Swift and Kotlin follow the same cases. */
export function shareStatus(note: NoteSharingFields | undefined, meId: string | null | undefined): ShareStatus {
  if (!note) return { kind: "none" };
  if (isSharedWithMe(note, meId)) {
    return { kind: "sharedBy", name: note.ownerName?.trim() || null, role: note.role === "editor" ? "editor" : "viewer" };
  }
  const people = note.memberCount ?? 0;
  const link = note.linkAccess ?? (note.sharedAt ? "view" : "none");
  if (people > 0 && link !== "none") return { kind: "peopleAndLink", count: people };
  if (people > 0) return { kind: "people", count: people };
  if (link !== "none") return { kind: "link", access: link };
  return { kind: "none" };
}

/** Whether opening this note's editor should prefetch its sharing (it has people). */
export function shouldPrefetchSharing(note: NoteSharingFields | undefined, meId: string | null | undefined): boolean {
  return isSharedWithMe(note, meId) || (note?.memberCount ?? 0) > 0;
}

// ── The No access screen (share-sheet plan, part 5) ──────────────────────

export type NoAccessReason = "link" | "signedOut" | "invite" | "removed";

/** The screen's copy: title, body, and the action under it. */
export function noAccessCopy(
  reason: NoAccessReason,
  savedCopy = false,
): { title: string; body: string; action: "back" | "signIn" } {
  switch (reason) {
    case "link":
      return { title: "noAccess.title", body: "noAccess.linkBody", action: "back" };
    case "signedOut":
      return { title: "noAccess.title", body: "noAccess.signedOutBody", action: "signIn" };
    case "invite":
      return { title: "noAccess.inviteTitle", body: "noAccess.inviteBody", action: "back" };
    case "removed":
      return {
        title: "noAccess.removedTitle",
        body: savedCopy ? "noAccess.removedCopyBody" : "noAccess.removedBody",
        action: "back",
      };
  }
}

// ── Access notices (live `access`, 4403, a pulled removal) ───────────────

export type AccessNotice =
  | { kind: "role"; noteId: string; role: NoteRole }
  | { kind: "removed"; noteId: string; savedCopy: boolean }
  | { kind: "savedCopy"; noteId: string };

export const ACCESS_NOTICE_EVENT = "caliu:sharing-access";

export function notifyAccess(notice: AccessNotice): void {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent<AccessNotice>(ACCESS_NOTICE_EVENT, { detail: notice }));
}

/** The toast for a notice (an i18n key), or null when nothing is said. */
export function accessNoticeKey(notice: AccessNotice): string | null {
  switch (notice.kind) {
    case "role":
      if (notice.role === "viewer") return "sharing.accessNowViewer";
      if (notice.role === "editor") return "sharing.accessNowEditor";
      return null;
    case "removed":
      return notice.savedCopy ? "sharing.unsentSavedAsCopy" : "sharing.accessRemoved";
    case "savedCopy":
      return "sharing.unsentSavedAsCopy";
  }
}

// ── Deep links ────────────────────────────────────────────────────────────

/** Where to go after sign-in for an invite or a link join, kept through `?next=`. */
export function inviteNextPath(token: string): string {
  return `/app/invite/${encodeURIComponent(token)}`;
}
